import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'

import { CheckCircleFilled, DeleteOutlined, EditOutlined, ExclamationCircleFilled } from '@ant-design/icons'
import { Button, Card, Col, Divider, Modal, Row, Space, Typography } from 'antd'
import { CardTabListType } from 'antd/lib/card'

import {
  BillingProduct,
  BillingSubscription,
  BillingSubscriptionUpdate,
  BillingTransaction
} from '@pollination-solutions/pollination-sdk'

import { useAccount } from 'hooks'
import useBillingAPI from 'hooks/useBillingAPI'
import { usePaddle } from 'hooks/usePaddleBilling'
import { calculateResourcesByType, formatCurrency, getSubscriptionBillingFrequency } from './billingUtils'
import { SubscriptionItemsEditor } from './SubscriptionItemsEditor'
import { SubscriptionOverview } from './SubscriptionOverview'
import { SubscriptionTransactions } from './SubscriptionTransactions'

type Props = {
  products: BillingProduct[]
  subscription: BillingSubscription
  existingSubscriptions?: BillingSubscription[]
  onUpdateSuccess?: () => void
}

// Helper functions for subscription cancellation status
const isPendingCancellation = (subscription: BillingSubscription): boolean => {
  return !!(subscription.scheduled_change &&
    (subscription.scheduled_change as any)?.action === 'cancel')
}

const getCancellationDate = (subscription: BillingSubscription): string | null => {
  if (!isPendingCancellation(subscription)) return null
  const effectiveAt = (subscription.scheduled_change as any)?.effective_at
  return effectiveAt ? new Date(effectiveAt).toLocaleDateString() : null
}


export const BillingSubscriptionCard: React.FC<Props> = ({ products, subscription, existingSubscriptions = [], onUpdateSuccess }) => {
  const { account } = useAccount()
  const { updateBillingSubscription, listBillingSubscriptionTransactions, cancelBillingSubscription, reactivateBillingSubscription, previewBillingSubscription, updateSubscriptionPaymentMethod } = useBillingAPI()
  const { openPaymentMethodUpdate } = usePaddle()

  const [activeKey, setActiveKey] = useState('subscription-details')
  const [editMode, setEditMode] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [editedItems, setEditedItems] = useState<{ [itemId: string]: { quantity: number; priceId?: string } }>({})
  const [removedItems, setRemovedItems] = useState<Set<number>>(new Set())
  const [newItems, setNewItems] = useState<{ productId?: string; priceId?: string; quantity: number; isEditing: boolean }[]>([])
  const [originalSubscriptionState, setOriginalSubscriptionState] = useState<{ [itemId: string]: { quantity: number; priceId: string } } | null>(null)
  const [transactions, setTransactions] = useState<BillingTransaction[]>([])
  const [transactionsLoading, setTransactionsLoading] = useState(false)
  const [transactionsTabInitialized, setTransactionsTabInitialized] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false)
  const [itemToRemove, setItemToRemove] = useState<number | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [showReactivateModal, setShowReactivateModal] = useState(false)
  const [isReactivating, setIsReactivating] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<any>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  // Check if subscription is pending cancellation
  const pendingCancellation = isPendingCancellation(subscription)
  const cancellationDate = getCancellationDate(subscription)

  const hasSubscription = Boolean(subscription)

  const visibleTransactions = useMemo(() => {
    // Filter out payment method change transactions
    return transactions.filter(d => d.origin !== 'subscription_payment_method_change')
  }, [transactions])


  // Get all currently selected price IDs to avoid duplicates
  const selectedPriceIds = useMemo(() => {
    const priceIds = new Set<string>()

    // Add existing item prices (including edits), excluding removed items
    subscription.items?.forEach((item, index) => {
      // Skip removed items
      if (removedItems.has(index)) return

      const editedItem = editedItems[index]
      const priceId = editedItem?.priceId || item.price.id
      if (priceId) priceIds.add(priceId)
    })

    // Add new item prices
    newItems.forEach(newItem => {
      if (newItem.priceId) priceIds.add(newItem.priceId)
    })

    return priceIds
  }, [subscription.items, editedItems, newItems, removedItems])

  // Get payment method info from the latest transaction
  const latestPaymentMethod = useMemo(() => {
    if (!transactions || transactions.length === 0) return null

    // Sort transactions by created_at descending to get the latest
    const sortedTransactions = [...transactions].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )

    const latestTransaction = sortedTransactions.filter(t => t.payments && t.payments.length > 0)[0]
    if (!latestTransaction?.payments || latestTransaction.payments.length === 0) return null

    // Get payment method from the first payment (most recent)
    const payment = latestTransaction.payments[0]
    return {
      type: payment.method_details?.type || 'Unknown',
      card: payment.method_details?.card,
      createdAt: latestTransaction.created_at
    }
  }, [transactions])

  // Calculate existing items with edits, excluding removed items
  const existingTotal = useMemo(() => subscription.items.reduce((total, item, index) => {
    // Skip removed items
    if (removedItems.has(index)) return total

    const editedItem = editedItems[index]
    const quantity = editedItem?.quantity || item.quantity
    const priceId = editedItem?.priceId || item.price.id

    // Find the price (either from current item or from products if changed)
    let priceAmount = item.price.unit_price?.amount || '0'
    if (editedItem?.priceId && editedItem.priceId !== item.price.id) {
      const product = products.find(p => p.id === item.product.id)
      const newPrice = product?.prices?.find(p => p.id === priceId)
      priceAmount = newPrice?.unit_price.amount || '0'
    }

    return total + (parseFloat(priceAmount) * quantity)
  }, 0), [subscription.items, editedItems, products, removedItems])

  // Calculate new items
  const newItemsTotal = useMemo(() => newItems.reduce((total, newItem) => {
    if (!newItem.productId || !newItem.priceId) return total
    const product = products.find(p => p.id === newItem.productId)
    const price = product?.prices?.find(p => p.id === newItem.priceId)
    const priceAmount = parseFloat(price?.unit_price.amount || '0')
    return total + (priceAmount * newItem.quantity)
  }, 0), [newItems, products])


  // Calculate total subscription value including edits and new items
  const subscriptionTotal = useMemo(() => {
    if (!subscription?.items) return formatCurrency(0, subscription?.currency_code || 'USD')
    return formatCurrency(existingTotal + newItemsTotal, subscription.currency_code)
  }, [existingTotal, newItemsTotal, subscription])

  // Get the subscription's billing frequency
  const subscriptionBillingFrequency = useMemo(() => {
    return getSubscriptionBillingFrequency(subscription)
  }, [subscription])

  // Calculate resources by type for the subscription
  const resourcesByType = useMemo(() => {
    return calculateResourcesByType(products, subscription.items, editedItems, newItems, removedItems)
  }, [products, subscription.items, editedItems, newItems, removedItems])

  // Check if there are any changes to the subscription
  const hasChanges = useMemo(() => {
    if (!originalSubscriptionState) return false

    // Check if any items were removed
    if (removedItems.size > 0) return true

    // Check if any new items were added
    if (newItems.length > 0) return true

    // Check if any existing items were actually changed from their original values
    for (const [index, editedItem] of Object.entries(editedItems)) {
      const originalItem = originalSubscriptionState[index]
      if (!originalItem) continue

      // Compare quantity
      if (editedItem.quantity !== originalItem.quantity) return true

      // Compare priceId
      if (editedItem.priceId && editedItem.priceId !== originalItem.priceId) return true
    }

    return false
  }, [editedItems, removedItems, newItems, originalSubscriptionState])


  const handleItemEdit = useCallback((itemIndex: number, field: 'quantity' | 'priceId', value: number | string) => {
    const item = subscription.items[itemIndex]
    if (!item) return

    setEditedItems(prev => ({
      ...prev,
      [itemIndex]: {
        ...prev[itemIndex],
        quantity: prev[itemIndex]?.quantity || item.quantity,
        priceId: prev[itemIndex]?.priceId || item.price.id,
        [field]: value
      }
    }))
  }, [subscription.items])

  const handleAddNewItem = useCallback(() => {
    setNewItems(prev => [...prev, { quantity: 1, isEditing: true }])
  }, [])

  const handleUpdateNewItem = useCallback((index: number, field: 'productId' | 'priceId' | 'quantity', value: string | number) => {

    setNewItems(prev => prev.map((item, i) => {
      if (i === index) {
        // If changing product, clear the price selection to avoid conflicts
        if (field === 'productId') {
          // Ensure productId is always a string
          return { ...item, [field]: String(value), priceId: undefined }
        }
        // For quantity, ensure it's a number; for others, use the value as is
        const typedValue = field === 'quantity' ? Number(value) : value
        return { ...item, [field]: typedValue }
      }
      return item
    }))
  }, [])

  const handleRemoveNewItem = useCallback((index: number) => {
    setNewItems(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleRemoveExistingItem = useCallback((index: number) => {
    setItemToRemove(index)
    setShowRemoveConfirmModal(true)
  }, [])

  useEffect(() => {
    if (itemToRemove !== null) {
      setRemovedItems(prev => new Set(prev.add(itemToRemove)))
      // Also remove from editedItems to clean up state
      setEditedItems(prev => {
        const newState = { ...prev }
        delete newState[itemToRemove]
        return newState
      })
    }
    setShowRemoveConfirmModal(false)
    setItemToRemove(null)
  }, [itemToRemove])

  const prepareUpdateData = useCallback(() => {
    if (!subscription) return null

    // Convert edited items to the format expected by the API, excluding removed items
    const updatedExistingItems: Array<{ price_id: string, quantity: number }> = []
    subscription.items.forEach((item, index) => {
      // Skip removed items
      if (removedItems.has(index)) return

      const edits = editedItems[index]
      updatedExistingItems.push({
        price_id: edits?.priceId || item.price.id,
        quantity: edits?.quantity || item.quantity
      })
    })

    // Add new items (only those with complete data)
    const newItemsForApi = newItems
      .filter(item => item.productId && item.priceId && item.quantity > 0)
      .map(item => ({
        price_id: item.priceId as string, // We know it's defined due to the filter above
        quantity: item.quantity
      }))

    const allItems = [...updatedExistingItems, ...newItemsForApi]

    return {
      items: allItems
    } as BillingSubscriptionUpdate
  }, [subscription, editedItems, newItems, removedItems])

  const handlePreviewChanges = useCallback(async () => {
    if (!subscription?.id) return

    const updateData = prepareUpdateData()
    if (!updateData) return

    setIsPreviewLoading(true)
    try {
      const preview = await previewBillingSubscription(subscription.id, updateData)
      setPreviewData(preview)
      setShowPreviewModal(true)
    } catch (error) {
      console.error('Failed to preview subscription changes:', error)
    } finally {
      setIsPreviewLoading(false)
    }
  }, [subscription?.id, prepareUpdateData, previewBillingSubscription])

  const handleConfirmChanges = useCallback(async () => {
    if (!subscription || !account) return

    const updateData = prepareUpdateData()
    if (!updateData) return

    setIsUpdating(true)
    try {
      await updateBillingSubscription(subscription.id, updateData)

      // Reset edit state
      setEditMode(false)
      setEditedItems({})
      setRemovedItems(new Set())
      setNewItems([])
      setOriginalSubscriptionState(null)
      setShowPreviewModal(false)
      setPreviewData(null)

      // Call success callback to refresh data
      if (onUpdateSuccess) {
        setTimeout(() => {
          onUpdateSuccess()
        }, 0)
      }
    } catch (error) {
      console.error('Failed to update subscription:', error)
    } finally {
      setIsUpdating(false)
    }
  }, [subscription, account, prepareUpdateData, updateBillingSubscription, onUpdateSuccess])

  const handleCancelEdit = useCallback(() => {
    setEditMode(false)
    setEditedItems({})
    setRemovedItems(new Set())
    setNewItems([])
    setOriginalSubscriptionState(null)
  }, [])

  const handleCancelSubscription = useCallback(async () => {
    if (!subscription?.id) return

    setIsCancelling(true)
    try {
      await cancelBillingSubscription(subscription.id)
      setShowCancelModal(false)
      onUpdateSuccess?.()
    } catch (error) {
      console.error('Failed to cancel subscription:', error)
    } finally {
      setIsCancelling(false)
    }
  }, [subscription?.id, cancelBillingSubscription, onUpdateSuccess])

  const handleReactivateSubscription = useCallback(async () => {
    if (!subscription?.id) return

    setIsReactivating(true)
    try {
      await reactivateBillingSubscription(subscription.id)
      setShowReactivateModal(false)
      onUpdateSuccess?.()
    } catch (error) {
      console.error('Failed to reactivate subscription:', error)
    } finally {
      setIsReactivating(false)
    }
  }, [subscription?.id, reactivateBillingSubscription, onUpdateSuccess])

  const loadTransactions = useCallback(async () => {
    if (!subscription?.id) return

    setTransactionsLoading(true)
    try {
      const transactionData = await listBillingSubscriptionTransactions(subscription.id)
      // Check if the response is an array of transactions
      if (Array.isArray(transactionData)) {
        setTransactions(transactionData)
      } else if (transactionData && typeof transactionData === 'object' && 'detail' in transactionData) {
        // Handle API error response
        console.error('API error:', transactionData)
        setTransactions([])
      } else {
        // Handle unexpected response format
        console.warn('Unexpected transaction data format:', transactionData)
        setTransactions([])
      }
    } catch (error) {
      console.error('Failed to load transactions:', error)
      setTransactions([]) // Reset to empty array on error
    } finally {
      setTransactionsLoading(false)
    }
  }, [subscription?.id, listBillingSubscriptionTransactions])

  // Load transactions automatically when subscription loads
  useEffect(() => {
    if (subscription?.id && transactions.length === 0 && !transactionsLoading) {
      loadTransactions()
    }
  }, [subscription?.id, transactions.length, transactionsLoading, loadTransactions])

  // Load transactions when the transactions tab is selected (for refresh/reload scenarios)
  useEffect(() => {
    if (activeKey === 'transactions' && !transactionsTabInitialized) {
      // Add a small delay to prevent ResizeObserver issues
      const timer = setTimeout(() => {
        setTransactionsTabInitialized(true)
        if (transactions.length === 0) {
          loadTransactions()
        }
      }, 150)

      return () => clearTimeout(timer)
    }
  }, [activeKey, transactionsTabInitialized, transactions.length, loadTransactions])

  // Payment method update handler
  const handleUpdatePaymentMethod = useCallback(async () => {
    if (!subscription?.id) return
    try {
      const transaction = await updateSubscriptionPaymentMethod(subscription.id)
      // Open Paddle checkout for payment method update
      openPaymentMethodUpdate({
        transactionId: transaction.id,
      })
    } catch (error) {
      console.error('Failed to update payment method:', error)
    }
  }, [subscription?.id, updateSubscriptionPaymentMethod, openPaymentMethodUpdate])

  const { tabs, content } = useMemo(() => {
    const tabs: CardTabListType[] = []
    const content: { [index: string]: ReactElement } = {}

    if (hasSubscription) {
      tabs.push({
        key: 'subscription-details',
        tab: 'Subscription Details'
      })

      tabs.push({
        key: 'transactions',
        tab: `Transactions ${visibleTransactions.length > 0 ? `(${visibleTransactions.length})` : ''}`
      })


      content['subscription-details'] = (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <SubscriptionOverview
            subscription={subscription}
            subscriptionTotal={subscriptionTotal}
            subscriptionBillingFrequency={subscriptionBillingFrequency}
            latestPaymentMethod={latestPaymentMethod}
            onUpdatePaymentMethod={handleUpdatePaymentMethod}
          />

          <Divider style={{ margin: '8px 0' }} />

          {/* Subscription Items */}
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {!editMode && (
              <>
                <Space style={{ justifyContent: 'flex-start', width: '100%' }}>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => {
                      // Capture the original state when entering edit mode
                      const originalState: { [itemId: string]: { quantity: number; priceId: string } } = {}
                      subscription.items?.forEach((item, index) => {
                        originalState[index] = {
                          quantity: item.quantity,
                          priceId: item.price.id
                        }
                      })
                      setOriginalSubscriptionState(originalState)
                      setEditMode(true)
                    }}
                    disabled={pendingCancellation}
                  >
                    Edit Subscription
                  </Button>
                  {pendingCancellation ? (
                    <Button
                      type="primary"
                      icon={<CheckCircleFilled />}
                      onClick={() => setShowReactivateModal(true)}
                      size="small"
                    >
                      Reactivate Subscription
                    </Button>
                  ) : (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setShowCancelModal(true)}
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </Space>
              </>
            )}

            <SubscriptionItemsEditor
              subscription={subscription}
              products={products}
              existingSubscriptions={existingSubscriptions}
              editMode={editMode}
              editedItems={editedItems}
              removedItems={removedItems}
              newItems={newItems}
              onItemEdit={handleItemEdit}
              onAddNewItem={handleAddNewItem}
              onRemoveNewItem={handleRemoveNewItem}
              onUpdateNewItem={handleUpdateNewItem}
              onRemoveExistingItem={handleRemoveExistingItem}
              selectedPriceIds={selectedPriceIds}
              subscriptionBillingFrequency={subscriptionBillingFrequency}
              onEnterEditMode={() => setEditMode(true)}
            />

            {editMode && (
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button 
                  type="primary" 
                  onClick={handlePreviewChanges} 
                  loading={isPreviewLoading} 
                  disabled={!hasChanges}>
                  Preview Changes
                </Button>
              </Space>
            )}
          </Space>
        </Space >
      )

      // Transactions tab content
      content['transactions'] = (
        <SubscriptionTransactions
          transactions={visibleTransactions}
          transactionsLoading={transactionsLoading}
          transactionsTabInitialized={transactionsTabInitialized}
          onRefreshTransactions={loadTransactions}
        />
      )

    }

    return { tabs, content }
  }, [subscription, products, hasSubscription, editMode, editedItems, removedItems, subscriptionTotal, isPreviewLoading, handleItemEdit, handlePreviewChanges, handleCancelEdit, newItems, handleRemoveNewItem, handleAddNewItem, handleRemoveExistingItem, handleUpdateNewItem, selectedPriceIds, resourcesByType, subscriptionBillingFrequency, transactions, transactionsLoading, transactionsTabInitialized, loadTransactions, existingSubscriptions, latestPaymentMethod, handleUpdatePaymentMethod])

  const title = pendingCancellation
    ? `${subscriptionBillingFrequency === 'yearly' ? 'Yearly' : 'Monthly'} Subscription - Pending Cancellation`
    : `${subscriptionBillingFrequency === 'yearly' ? 'Yearly' : 'Monthly'} Subscription`

  const description = pendingCancellation
    ? `Subscription will be cancelled on ${cancellationDate}. You can reactivate it below.`
    : 'Manage your subscription details, quantities, and pricing'

  return (
    <>
      <Card
        loading={isUpdating}
        title={
          <Space direction='vertical' style={{ gap: '0px' }}>
            <Space>
              {pendingCancellation ? (
                <ExclamationCircleFilled style={{ color: '#fa8c16' }} />
              ) : (
                <CheckCircleFilled style={{ color: 'rgb(82, 196, 26)' }} />
              )}
              <Typography.Title level={5} style={{ marginBottom: 0 }}>
                {title}
              </Typography.Title>
            </Space>
            <Typography.Text type='secondary' style={{ fontSize: '14px', fontWeight: '400' }}>
              {description}
            </Typography.Text>
          </Space>
        }
        tabList={tabs}
        activeTabKey={activeKey}
        onTabChange={key => {
          setActiveKey(key)
        }}
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          border: pendingCancellation
            ? '2px solid #fa8c16' // Orange border for pending cancellation
            : '2px solid #52c41a' // Green border to indicate new billing
        }}
        bodyStyle={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
        tabProps={{
          size: 'small',
        }}
      >
        {content && content[activeKey]}
      </Card>

      {/* Cancel Subscription Confirmation Modal */}
      <Modal
        title="Cancel Subscription"
        open={showCancelModal}
        onCancel={() => setShowCancelModal(false)}
        footer={[
          <Button key="back" onClick={() => setShowCancelModal(false)}>
            Keep Subscription
          </Button>,
          <Button
            key="submit"
            danger
            type="primary"
            loading={isCancelling}
            onClick={handleCancelSubscription}
          >
            Yes, Cancel Subscription
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
        </div>
        <Typography.Paragraph>
          Are you sure you want to cancel this subscription? This will:
        </Typography.Paragraph>
        <ul style={{ marginLeft: '16px' }}>
          <li>Schedule the cancelation of your subscription for the end of the current billing period</li>
          <li>Stop all future billing</li>
          <li>Can be reversed before the end of the current billing period</li>
        </ul>
        <Typography.Paragraph>
          <strong>Subscription ID:</strong> <Typography.Text code>{subscription.id}</Typography.Text>
        </Typography.Paragraph>
      </Modal>

      {/* Reactivate Subscription Confirmation Modal */}
      <Modal
        title="Reactivate Subscription"
        open={showReactivateModal}
        onCancel={() => setShowReactivateModal(false)}
        footer={[
          <Button key="back" onClick={() => setShowReactivateModal(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isReactivating}
            onClick={handleReactivateSubscription}
          >
            Yes, Reactivate Subscription
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Typography.Text strong style={{ color: '#52c41a' }}>
            ✅ Great choice!
          </Typography.Text>
        </div>
        <Typography.Paragraph>
          Are you sure you want to reactivate this subscription? This will:
        </Typography.Paragraph>
        <ul style={{ marginLeft: '16px' }}>
          <li>Cancel the scheduled cancellation</li>
          <li>Continue your subscription and billing</li>
          <li>Maintain access to all subscription resources</li>
        </ul>
        <Typography.Paragraph>
          Your subscription will continue from where it left off, and you will be billed according to your normal schedule.
        </Typography.Paragraph>
        <Typography.Paragraph>
          <strong>Subscription ID:</strong> <Typography.Text code>{subscription.id}</Typography.Text>
        </Typography.Paragraph>
      </Modal>

      {/* Preview Changes Modal */}
      <Modal
        title="Preview Subscription Changes"
        open={showPreviewModal}
        onCancel={() => {
          setShowPreviewModal(false)
          setPreviewData(null)
        }}
        width={600}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setShowPreviewModal(false)
              setPreviewData(null)
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isUpdating}
            onClick={handleConfirmChanges}
          >
            Confirm Changes
          </Button>,
        ]}
      >
        {previewData ? (
          <div>
            <Typography.Paragraph>
              Please review your subscription changes before confirming:
            </Typography.Paragraph>

            {/* Immediate Transaction */}
            {previewData.immediate_transaction && (
              <div style={{ backgroundColor: '#fff7e6', padding: '16px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #ffd591' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <Typography.Title level={5} style={{ margin: 0, color: '#fa8c16' }}>
                    ⚡ Immediate Charge
                  </Typography.Title>
                  <Typography.Text strong style={{ color: '#fa8c16' }}>
                    Due Today
                  </Typography.Text>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography.Text>Subtotal:</Typography.Text>
                  <Typography.Text strong>
                    {formatCurrency(previewData.immediate_transaction.details?.totals?.subtotal || 0, subscription.currency_code)}
                  </Typography.Text>
                </div>

                {previewData.immediate_transaction.details?.totals?.tax && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Typography.Text>VAT/Tax:</Typography.Text>
                    <Typography.Text>
                      {formatCurrency(previewData.immediate_transaction.details.totals.tax, subscription.currency_code)}
                    </Typography.Text>
                  </div>
                )}

                {previewData.immediate_transaction.details?.totals?.credit && parseFloat(previewData.immediate_transaction.details.totals.credit) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Typography.Text style={{ color: '#52c41a' }}>Credit Applied:</Typography.Text>
                    <Typography.Text style={{ color: '#52c41a' }}>
                      -{formatCurrency(previewData.immediate_transaction.details.totals.credit, subscription.currency_code)}
                    </Typography.Text>
                  </div>
                )}

                <Divider style={{ margin: '12px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text strong style={{ fontSize: '16px' }}>Total Due Today:</Typography.Text>
                  <Typography.Text strong style={{ fontSize: '16px', color: '#fa8c16' }}>
                    {formatCurrency(previewData.immediate_transaction.details?.totals?.grand_total || previewData.immediate_transaction.details?.totals?.total || 0, subscription.currency_code)}
                  </Typography.Text>
                </div>

                {/* Immediate Transaction Items */}
                {previewData.immediate_transaction.details?.line_items && previewData.immediate_transaction.details.line_items.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <Typography.Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                      Items:
                    </Typography.Text>
                    {previewData.immediate_transaction.details.line_items.map((item: any, index: number) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        fontSize: '12px'
                      }}>
                        <div>
                          <Typography.Text style={{ fontSize: '12px' }}>{item.product?.name}</Typography.Text>
                          <Typography.Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                            × {item.quantity}
                          </Typography.Text>
                        </div>
                        <Typography.Text style={{ fontSize: '12px' }}>
                          {formatCurrency(item.totals?.total || 0, subscription.currency_code)}
                        </Typography.Text>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Next Transaction */}
            {previewData.next_transaction && (
              <div style={{ backgroundColor: '#f6ffed', padding: '16px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #b7eb8f' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <Typography.Title level={5} style={{ margin: 0, color: '#52c41a' }}>
                    🔄 Next Billing Cycle
                  </Typography.Title>
                  <Typography.Text strong style={{ color: '#52c41a' }}>
                    {previewData.next_billed_at ? new Date(previewData.next_billed_at).toLocaleDateString() : 'Next cycle'}
                  </Typography.Text>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography.Text>Subtotal:</Typography.Text>
                  <Typography.Text strong>
                    {formatCurrency(previewData.next_transaction.details?.totals?.subtotal || 0, subscription.currency_code)}
                  </Typography.Text>
                </div>

                {previewData.next_transaction.details?.totals?.tax && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Typography.Text>VAT/Tax:</Typography.Text>
                    <Typography.Text>
                      {formatCurrency(previewData.next_transaction.details.totals.tax, subscription.currency_code)}
                    </Typography.Text>
                  </div>
                )}

                <Divider style={{ margin: '12px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text strong style={{ fontSize: '16px' }}>Next Cycle Total:</Typography.Text>
                  <Typography.Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                    {formatCurrency(previewData.next_transaction.details?.totals?.grand_total || previewData.next_transaction.details?.totals?.total || 0, subscription.currency_code)}
                  </Typography.Text>
                </div>

                {/* Next Transaction Items */}
                {previewData.next_transaction.details?.line_items && previewData.next_transaction.details.line_items.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <Typography.Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                      Items:
                    </Typography.Text>
                    {previewData.next_transaction.details.line_items.map((item: any, index: number) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        fontSize: '12px'
                      }}>
                        <div>
                          <Typography.Text style={{ fontSize: '12px' }}>{item.product?.name}</Typography.Text>
                          <Typography.Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                            × {item.quantity}
                          </Typography.Text>
                        </div>
                        <Typography.Text style={{ fontSize: '12px' }}>
                          {formatCurrency(item.totals?.total || 0, subscription.currency_code)}
                        </Typography.Text>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px' }}>
              <Typography.Text style={{ fontSize: '12px', color: '#1890ff' }}>
                💡 Changes will take effect immediately. You will be charged the &quot;Immediate Charge&quot; amount today, and future billing will follow the &quot;Next Billing Cycle&quot; schedule.
              </Typography.Text>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Typography.Text type="secondary">Loading preview...</Typography.Text>
          </div>
        )}
      </Modal>

    </>
  )
}